export type BlogCategory = 'Features' | 'Guides & Tips' | 'Updates';

export interface BlogPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  author: string;
  category: BlogCategory;
  tags: string[];
  readingTime: number;
  featuredImage?: string;
  content: ContentSection[];
}

export interface ContentSection {
  type: 'paragraph' | 'heading' | 'list';
  text?: string;
  items?: string[];
}

export const blogCategories: BlogCategory[] = ['Features', 'Guides & Tips', 'Updates'];

export const categoryColors: Record<BlogCategory, { bg: string; text: string; border: string }> = {
  'Features': {
    bg: 'bg-primary/10 dark:bg-primary/20',
    text: 'text-primary',
    border: 'border-primary/20 dark:border-primary/30'
  },
  'Guides & Tips': {
    bg: 'bg-green-500/10 dark:bg-green-500/20',
    text: 'text-green-600 dark:text-green-400',
    border: 'border-green-500/20 dark:border-green-500/30'
  },
  'Updates': {
    bg: 'bg-orange-500/10 dark:bg-orange-500/20',
    text: 'text-orange-600 dark:text-orange-400',
    border: 'border-orange-500/20 dark:border-orange-500/30'
  }
};

export const blogPosts: BlogPost[] = [
  {
    id: 1,
    slug: "getting-started-employee-management",
    title: "Getting Started with Employee Management",
    excerpt: "Learn how to efficiently manage your team, track attendance, and streamline your HR processes with our comprehensive employee management tools.",
    date: "2025-11-10",
    author: "Sarah Johnson",
    category: "Features",
    tags: ["employee management", "HR", "attendance", "team management", "onboarding"],
    readingTime: 5,
    content: [
      {
        type: 'paragraph',
        text: "Managing employees effectively is crucial for any business's success. In today's fast-paced work environment, having the right tools and strategies can make all the difference between a thriving team and organizational chaos."
      },
      {
        type: 'heading',
        text: "Why Employee Management Matters"
      },
      {
        type: 'paragraph',
        text: "Effective employee management goes beyond simply tracking hours and assigning tasks. It's about creating an environment where your team can thrive, grow, and contribute their best work. When done right, proper employee management leads to increased productivity, higher job satisfaction, and lower turnover rates."
      },
      {
        type: 'heading',
        text: "Key Features of Modern Employee Management"
      },
      {
        type: 'list',
        items: [
          "Automated time tracking and attendance monitoring",
          "Digital onboarding processes that save time and reduce paperwork",
          "Performance tracking and review systems",
          "Easy communication channels between managers and staff",
          "Payroll integration for accurate and timely payments"
        ]
      },
      {
        type: 'heading',
        text: "Getting Started: Best Practices"
      },
      {
        type: 'paragraph',
        text: "Start by clearly defining roles and responsibilities for each team member. Use digital tools to maintain up-to-date employee records, including contact information, certifications, and training completion. Set up regular check-ins and performance reviews to ensure everyone is aligned with company goals."
      },
      {
        type: 'paragraph',
        text: "Implement a system for tracking time off requests and approvals. This transparency helps prevent scheduling conflicts and ensures adequate coverage at all times. Consider using mobile apps that allow employees to clock in/out, request time off, and view their schedules from anywhere."
      },
      {
        type: 'heading',
        text: "The Bottom Line"
      },
      {
        type: 'paragraph',
        text: "Investing in proper employee management tools and practices pays dividends in the form of a more engaged, productive workforce. By streamlining administrative tasks, you free up time to focus on what really matters: growing your business and supporting your team's success."
      }
    ]
  },
  {
    id: 2,
    slug: "maximizing-customer-loyalty-programs",
    title: "Maximizing Customer Loyalty Programs",
    excerpt: "Discover best practices for creating engaging loyalty programs that keep customers coming back. From digital passes to reward strategies.",
    date: "2025-11-08",
    author: "Michael Chen",
    category: "Guides & Tips",
    tags: ["loyalty programs", "customer retention", "digital wallet", "rewards", "engagement"],
    readingTime: 6,
    content: [
      {
        type: 'paragraph',
        text: "In an increasingly competitive marketplace, customer loyalty programs have evolved from simple punch cards to sophisticated digital experiences. Today's consumers expect more than just discounts—they want personalized rewards and seamless interactions."
      },
      {
        type: 'heading',
        text: "The Evolution of Loyalty Programs"
      },
      {
        type: 'paragraph',
        text: "Traditional paper-based loyalty cards are quickly becoming obsolete. Modern customers prefer digital solutions that integrate with their smartphones, offering convenience and real-time updates. Apple Wallet and Google Wallet passes have revolutionized how businesses connect with their loyal customers."
      },
      {
        type: 'heading',
        text: "Building an Effective Loyalty Strategy"
      },
      {
        type: 'list',
        items: [
          "Offer tiered rewards to encourage repeat visits and higher spending",
          "Personalize offers based on customer purchase history and preferences",
          "Make redemption simple and instant to enhance customer satisfaction",
          "Use push notifications to remind customers of their rewards and special offers",
          "Track customer engagement metrics to continuously improve your program"
        ]
      },
      {
        type: 'heading',
        text: "Digital Wallet Integration"
      },
      {
        type: 'paragraph',
        text: "Implementing digital wallet passes ensures your loyalty program is always accessible to customers. Unlike physical cards that can be lost or forgotten, digital passes live on smartphones and can be updated remotely with new offers, point balances, and exclusive deals."
      },
      {
        type: 'paragraph',
        text: "The best loyalty programs create a sense of exclusivity and value. Consider offering birthday rewards, early access to sales, or special events for top-tier members. These emotional connections transform one-time buyers into brand advocates."
      },
      {
        type: 'heading',
        text: "Measuring Success"
      },
      {
        type: 'paragraph',
        text: "Track key metrics such as repeat purchase rate, average customer lifetime value, and program enrollment rates. Use this data to refine your offerings and ensure your loyalty program delivers real value to both your customers and your business."
      }
    ]
  },
  {
    id: 3,
    slug: "future-of-shift-scheduling",
    title: "The Future of Shift Scheduling",
    excerpt: "Explore how modern scheduling tools are revolutionizing workforce management with AI-powered optimization and real-time updates.",
    date: "2025-11-05",
    author: "Emma Davis",
    category: "Features",
    tags: ["shift scheduling", "workforce management", "AI", "automation", "productivity"],
    readingTime: 5,
    content: [
      {
        type: 'paragraph',
        text: "Shift scheduling has long been one of the most time-consuming and frustrating aspects of workforce management. But with advances in technology and artificial intelligence, the future of scheduling is looking brighter than ever."
      },
      {
        type: 'heading',
        text: "The Problems with Traditional Scheduling"
      },
      {
        type: 'paragraph',
        text: "Manual scheduling often leads to conflicts, overstaffing or understaffing, and employee dissatisfaction. Managers spend hours each week creating schedules, only to have them disrupted by last-minute changes, sick calls, or time-off requests. This inefficiency costs businesses money and creates stress for everyone involved."
      },
      {
        type: 'heading',
        text: "How Technology is Changing the Game"
      },
      {
        type: 'list',
        items: [
          "AI-powered algorithms that optimize schedules based on predicted demand",
          "Real-time notifications for schedule changes and open shifts",
          "Employee self-service portals for shift swaps and time-off requests",
          "Integration with POS systems to align staffing with actual business needs",
          "Mobile accessibility allowing managers to adjust schedules from anywhere"
        ]
      },
      {
        type: 'heading',
        text: "The Benefits of Smart Scheduling"
      },
      {
        type: 'paragraph',
        text: "Modern scheduling tools reduce the time spent on schedule creation by up to 80%. They automatically consider employee availability, labor laws, and business requirements to create optimal schedules. Employees gain more control over their work-life balance through easy shift swapping and transparent schedule access."
      },
      {
        type: 'paragraph',
        text: "Predictive analytics can forecast busy periods based on historical data, ensuring you always have adequate staffing. This prevents the costly problems of overstaffing during slow periods or being short-handed during rush times."
      },
      {
        type: 'heading',
        text: "Looking Ahead"
      },
      {
        type: 'paragraph',
        text: "As AI and machine learning continue to advance, we can expect even more sophisticated scheduling solutions. Future systems will likely incorporate employee skill development, automatically suggesting training opportunities and cross-training schedules to build a more versatile workforce."
      }
    ]
  },
  {
    id: 4,
    slug: "boost-review-collection-strategy",
    title: "Boosting Your Review Collection Strategy",
    excerpt: "Proven techniques to encourage more customer reviews, improve your online reputation, and respond effectively to feedback.",
    date: "2025-11-03",
    author: "James Wilson",
    category: "Guides & Tips",
    tags: ["reviews", "reputation management", "customer feedback", "online presence", "marketing"],
    readingTime: 6,
    content: [
      {
        type: 'paragraph',
        text: "Online reviews have become the modern word-of-mouth, influencing purchasing decisions more than traditional advertising. Yet many businesses struggle to collect reviews consistently. Here's how to build an effective review collection strategy."
      },
      {
        type: 'heading',
        text: "Why Reviews Matter More Than Ever"
      },
      {
        type: 'paragraph',
        text: "Studies show that 93% of consumers read online reviews before making a purchase decision. Businesses with higher ratings and more reviews enjoy better search rankings, increased trust, and higher conversion rates. Your review profile is essentially your digital storefront."
      },
      {
        type: 'heading',
        text: "Effective Review Collection Techniques"
      },
      {
        type: 'list',
        items: [
          "Ask for reviews at the right moment—immediately after a positive interaction",
          "Make the process as simple as possible with direct links and clear instructions",
          "Send automated follow-up emails to customers after their purchase or service",
          "Train staff to mention reviews during checkout or service completion",
          "Offer incentives for honest feedback (without violating platform guidelines)"
        ]
      },
      {
        type: 'heading',
        text: "The Art of Responding to Reviews"
      },
      {
        type: 'paragraph',
        text: "Collecting reviews is only half the battle. How you respond to them matters just as much. Thank customers for positive reviews and address negative feedback promptly and professionally. A well-handled negative review can actually improve your reputation by showing that you care about customer satisfaction."
      },
      {
        type: 'paragraph',
        text: "Create templates for common review scenarios, but personalize each response. Generic replies feel insincere and can do more harm than good. Take the time to address specific points mentioned in the review and offer solutions when applicable."
      },
      {
        type: 'heading',
        text: "Turning Feedback Into Action"
      },
      {
        type: 'paragraph',
        text: "Use review data to identify patterns and areas for improvement. If multiple customers mention the same issue, it's time to address it. Share positive reviews with your team to boost morale and recognize excellent service. Your review strategy should be a continuous cycle of collection, response, and improvement."
      }
    ]
  },
  {
    id: 5,
    slug: "digital-wallet-integration-guide",
    title: "Digital Wallet Integration Guide",
    excerpt: "Step-by-step instructions for implementing Apple Wallet and Google Wallet passes to enhance your customer experience.",
    date: "2025-11-01",
    author: "Lisa Martinez",
    category: "Guides & Tips",
    tags: ["digital wallet", "Apple Wallet", "Google Wallet", "mobile passes", "technology"],
    readingTime: 7,
    content: [
      {
        type: 'paragraph',
        text: "Digital wallets have transformed how customers interact with businesses. From loyalty cards to event tickets, digital passes offer convenience and engagement opportunities that paper-based solutions simply cannot match."
      },
      {
        type: 'heading',
        text: "Understanding Digital Wallet Passes"
      },
      {
        type: 'paragraph',
        text: "Digital wallet passes are more than just digital versions of physical cards. They're interactive, updatable, and can send location-based notifications to users. When a customer walks near your store, their pass can remind them of available rewards or special offers."
      },
      {
        type: 'heading',
        text: "Types of Passes You Can Create"
      },
      {
        type: 'list',
        items: [
          "Loyalty cards with real-time point balance updates",
          "Coupons with expiration countdowns and redemption tracking",
          "Event tickets with check-in capabilities and schedule updates",
          "Membership cards with tier status and exclusive benefits",
          "Gift cards with remaining balance visible at a glance"
        ]
      },
      {
        type: 'heading',
        text: "Implementation Best Practices"
      },
      {
        type: 'paragraph',
        text: "Start with a clear use case and design your pass accordingly. Keep the design clean and on-brand—your pass represents your business in customers' wallets alongside major brands. Use the front of the pass for essential information and the back for detailed terms or instructions."
      },
      {
        type: 'paragraph',
        text: "Implement QR codes or barcodes for easy scanning at point of sale. Test your passes thoroughly on both iOS and Android devices to ensure they display correctly and function as expected. Remember that updates to passes happen in real-time, so you can correct issues or update information after distribution."
      },
      {
        type: 'heading',
        text: "Maximizing Engagement"
      },
      {
        type: 'paragraph',
        text: "Use push notifications strategically—too many will cause customers to delete your pass. Focus on high-value messages like reward availability, special events, or expiring offers. Track pass installation and engagement rates to measure the success of your digital wallet strategy and make data-driven improvements."
      }
    ]
  },
  {
    id: 6,
    slug: "building-strong-recruitment-pipeline",
    title: "Building a Strong Recruitment Pipeline",
    excerpt: "Tips and strategies for attracting top talent, streamlining your hiring process, and creating compelling job postings.",
    date: "2025-10-28",
    author: "David Brown",
    category: "Features",
    tags: ["recruitment", "hiring", "talent acquisition", "HR", "job postings"],
    readingTime: 6,
    content: [
      {
        type: 'paragraph',
        text: "Finding and hiring the right people is one of the biggest challenges businesses face today. A strong recruitment pipeline ensures you always have access to qualified candidates when positions open up, reducing time-to-hire and improving hire quality."
      },
      {
        type: 'heading',
        text: "What is a Recruitment Pipeline?"
      },
      {
        type: 'paragraph',
        text: "A recruitment pipeline is a pool of potential candidates at various stages of the hiring process. Rather than starting from zero each time you need to hire, you maintain ongoing relationships with qualified individuals who might be the perfect fit for future positions."
      },
      {
        type: 'heading',
        text: "Building Your Pipeline"
      },
      {
        type: 'list',
        items: [
          "Create compelling job descriptions that highlight your company culture and benefits",
          "Leverage social media and professional networks to reach passive candidates",
          "Attend industry events and career fairs to build relationships",
          "Implement an employee referral program to tap into your team's networks",
          "Maintain a database of promising candidates from previous hiring cycles"
        ]
      },
      {
        type: 'heading',
        text: "Streamlining the Application Process"
      },
      {
        type: 'paragraph',
        text: "Make applying easy and mobile-friendly. Long, complicated application forms discourage qualified candidates. Collect only essential information initially and gather details later in the process. Provide clear timelines and keep candidates informed throughout the hiring process."
      },
      {
        type: 'paragraph',
        text: "Use applicant tracking systems to organize and evaluate candidates efficiently. Standardize your interview process with structured questions and evaluation criteria to ensure fair, consistent assessments. This also helps you identify the best talent based on objective measures rather than gut feeling."
      },
      {
        type: 'heading',
        text: "Nurturing Candidate Relationships"
      },
      {
        type: 'paragraph',
        text: "Even candidates you don't hire immediately can be valuable for future positions. Send periodic updates about your company, share relevant content, and maintain the relationship. When the right position opens up, you'll have a warm lead rather than starting cold outreach. Remember, every interaction with a candidate—whether they're hired or not—reflects on your employer brand."
      }
    ]
  }
];

// Helper function to get post by slug
export const getPostBySlug = (slug: string): BlogPost | undefined => {
  return blogPosts.find(post => post.slug === slug);
};

// Helper function to get related posts (same category, excluding current)
export const getRelatedPosts = (currentSlug: string, limit: number = 3): BlogPost[] => {
  const currentPost = getPostBySlug(currentSlug);
  if (!currentPost) return [];

  return blogPosts
    .filter(post => post.slug !== currentSlug && post.category === currentPost.category)
    .slice(0, limit);
};

// Helper function to search posts
export const searchPosts = (query: string): BlogPost[] => {
  const lowerQuery = query.toLowerCase();
  return blogPosts.filter(post =>
    post.title.toLowerCase().includes(lowerQuery) ||
    post.excerpt.toLowerCase().includes(lowerQuery) ||
    post.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
};

// Helper function to filter by category
export const filterByCategory = (category: BlogCategory | null): BlogPost[] => {
  if (!category) return blogPosts;
  return blogPosts.filter(post => post.category === category);
};
